/**
 * Registration-Tokens API.
 *
 * Backend: admin-api auf api.it-sce.de unter `/api/admin/tokens`. Response-
 * Wrapper ist `{success: true, data: ...}` — wir unwrappen `.data` damit
 * Caller mit nackten Typen arbeiten.
 *
 * Aufgerufen via Customer-Portal Staff-Sektion (gegated `useStaff`).
 */
import type { AxiosInstance } from 'axios'
import { request } from '../client'
import type {
  TToken,
  TTokenList,
  TTokenCreateRequest,
  TTokenUpdateRequest,
} from '../types/staff'

interface AdminEnvelope<T> {
  success: boolean
  data: T
  error?: string
}

export interface TokensApi {
  list: () => Promise<TTokenList>
  get: (id: number) => Promise<TToken>
  create: (req: TTokenCreateRequest) => Promise<TToken>
  update: (id: number, req: TTokenUpdateRequest) => Promise<TToken>
  /** Token deaktivieren / widerrufen (admin-api DELETE) */
  revoke: (id: number) => Promise<void>
}

export function createTokensApi(api: AxiosInstance): TokensApi {
  // Pfad relativ zur baseURL der übergebenen Axios-Instance.
  // Im Customer-Portal: baseURL = 'https://api.it-sce.de', Pfad = '/admin/tokens'.
  const base = '/admin/tokens'
  return {
    list: async () => {
      const env = await request<AdminEnvelope<TToken[]>>(api, { method: 'GET', url: base })
      return { tokens: env.data ?? [] }
    },
    get: async (id) => {
      const env = await request<AdminEnvelope<TToken>>(api, { method: 'GET', url: `${base}/${id}` })
      return env.data
    },
    create: async (req) => {
      const env = await request<AdminEnvelope<TToken>>(api, { method: 'POST', url: base, data: req })
      return env.data
    },
    update: async (id, req) => {
      const env = await request<AdminEnvelope<TToken>>(api, { method: 'PATCH', url: `${base}/${id}`, data: req })
      return env.data
    },
    revoke: async (id) => {
      await request<AdminEnvelope<unknown>>(api, { method: 'DELETE', url: `${base}/${id}` })
    },
  }
}
